import { ClockIcon, UserIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from 'src/components/ui/Button';
import { toast } from 'src/components/ui/toast';
import useAuth from 'src/hooks/useAuth';
import { apiGet, apiPost } from 'src/lib/api/axios';

type MembershipRequestStatus = 'pending' | 'approved' | 'declined' | null;

export default function GuestMembershipPrompt({ variant = 'inline' }: { variant?: 'inline' | 'banner' }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const tenantName = user?.getActiveTenant(pathname)?.name;
  const [requestStatus, setRequestStatus] = useState<MembershipRequestStatus>(null);
  const [, setRequestLoading] = useState(false);
  const [requestChecked, setRequestChecked] = useState(false);

  const checkExistingRequest = useCallback(async () => {
    try {
      const response = await apiGet<{ status: string } | null>('/tenant-management/membership-requests/my', {
        meta: { globalError: false, context: 'guest-membership-request-check' }
      });
      setRequestStatus((response.data?.status as MembershipRequestStatus) ?? null);
    } catch {
      // silently ignore — not critical
    } finally {
      setRequestChecked(true);
    }
  }, []);

  useEffect(() => {
    void checkExistingRequest();
  }, [checkExistingRequest]);

  const handleRequestMembership = useCallback(async () => {
    try {
      setRequestLoading(true);
      await apiPost(
        '/tenant-management/membership-requests',
        {},
        { meta: { globalError: false, context: 'guest-membership-request-create' } }
      );
      setRequestStatus('pending');
      toast.success('Membership request sent to organisation admins');
    } catch (error: unknown) {
      const detail =
        error && typeof error === 'object' && 'parsed' in error
          ? (error as { parsed?: { description?: string } }).parsed?.description
          : undefined;
      if (detail?.includes('already exists')) {
        setRequestStatus('pending');
      } else {
        toast.error(detail || 'Failed to send membership request');
      }
    } finally {
      setRequestLoading(false);
    }
  }, []);

  if (!requestChecked) return null;

  if (variant === 'banner') {
    return (
      <div className="flex flex-col gap-2.5 rounded-xl border border-border-default bg-primary-lightblue px-3.5 py-2.5 md:flex-row md:items-center md:gap-3">
        <p className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-secondary-blue">
          <UserIcon size={14} weight="fill" />
          Guest
        </p>
        <p className="min-w-0 text-sm text-content-default">
          As a guest of {tenantName ?? 'your organisation'}, you can only access resources shared with you by full
          members. You can request full membership from an organisation admin.
        </p>
        <div className="flex shrink-0 items-center gap-2 md:ml-auto">
          {requestStatus === 'pending' ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-content-subtle">
              <ClockIcon size={16} />
              Request pending
            </span>
          ) : (
            <Button variant="primary" size="md" text="Request access" onClick={handleRequestMembership} />
          )}
        </div>
      </div>
    );
  }

  if (requestStatus === 'pending') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-hover px-4 py-2.5 text-sm text-content-subtle">
        <ClockIcon size={16} weight="regular" />
        <span>Membership request pending admin review</span>
      </div>
    );
  }

  return (
    <p className="text-sm text-content-subtle">
      Are you an employee of {tenantName ?? 'your organisation'}?
      <button type="button" onClick={handleRequestMembership} className="ml-2 text-misc-link hover:underline">
        Request full membership.
      </button>
    </p>
  );
}
